import React from 'react';
import { Menu, X, ArrowRight, CheckCircle2, Building2, Store, Utensils, Construction, GraduationCap, Fuel, Crown, Camera, Globe, Clock, Users, BarChart3, Shield, Zap, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [hoveredIndustry, setHoveredIndustry] = React.useState(null);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const industries = [
    {
      icon: Building2,
      title: 'OOH Media & Billboards',
      pain: 'Scattered proof of campaign execution',
      benefit: 'GPS-tagged photos for every billboard',
      image: 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5',
      caseStudies: [
        'Billboard campaign execution verification',
        'Street furniture advertising compliance',
        'Transit shelter display monitoring',
        'Digital billboard content verification',
        'Installation and removal documentation',
        'Lighting condition checks'
      ]
    },
    {
      icon: Store,
      title: 'Retail & Grocery',
      pain: 'Inconsistent merchandising across locations',
      benefit: 'Standardized visual compliance checks',
      image: 'https://images.unsplash.com/photo-1578916171728-46686eac8d58',
      caseStudies: [
        'Daily verification of end-cap displays',
        'Visual stock control',
        'Promotional compliance audits',
        'Opening/closing checklists',
        'Brand-specific display verification'
      ]
    },
    {
      icon: Utensils,
      title: 'Restaurants & QSR',
      pain: 'No proof of cleanliness standards',
      benefit: 'Timestamped verification of operations',
      image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4',
      caseStudies: [
        'Kitchen and equipment audit',
        'Staff uniform compliance',
        'POP material verification',
        'Food safety checklists',
        'Drive-thru audits'
      ]
    },
    {
      icon: Fuel,
      title: 'Gas Station Networks',
      pain: 'Manual site inspection reporting',
      benefit: 'Automated structured photo audits',
      image: 'https://images.unsplash.com/photo-1567958451986-2de427a4a0be',
      caseStudies: [
        'Daily pump compliance (cleanliness, nozzles, signage)',
        'Fuel price signage verification',
        'Lighting and canopy inspection',
        'Emergency equipment inventory',
        'Convenience store promotional compliance'
      ]
    },
    {
      icon: Crown,
      title: 'Luxury Retail',
      pain: 'Brand standards not enforced',
      benefit: 'Visual proof of premium execution',
      image: 'https://images.unsplash.com/photo-1587582423116-ec07293f0395',
      caseStudies: [
        'Window displays and visual merchandising verification',
        'Seasonal campaigns and product launches compliance',
        'Store walkthrough for corporate image consistency',
        'Lighting and fixture condition audits',
        'Staff uniform, grooming, and service protocol verification'
      ]
    },
    {
      icon: GraduationCap,
      title: 'Schools & Universities',
      pain: 'Facility issues go untracked',
      benefit: 'Systematic campus condition monitoring',
      image: 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5',
      caseStudies: [
        'Classroom maintenance verification',
        'Safety audits (signage, fire extinguishers, evacuation routes)',
        'Branding and communication material checks',
        'AV inventory (projectors, tablets)',
        'Event preparation audits'
      ]
    },
    {
      icon: Construction,
      title: 'Construction & Facilities',
      pain: 'Progress photos lack context',
      benefit: 'Structured site documentation',
      image: 'https://images.unsplash.com/photo-1587582423116-ec07293f0395',
      caseStudies: [
        'Daily construction progress photos',
        'PPE and safety compliance',
        'Inspection visit documentation',
        'Sales room and model unit checks',
        'Installation testing verification'
      ]
    }
  ];

  const benefits = [
    {
      icon: Camera,
      title: 'Structured Photography',
      description: 'Every photo includes GPS, timestamp, user ID, and campaign metadata'
    },
    {
      icon: Globe,
      title: 'Scale Globally',
      description: 'Verify thousands of locations with your teams or POPapp photographers'
    },
    {
      icon: Clock,
      title: 'Indoor & Outdoor',
      description: 'Workflows for any environment - interiors, exteriors, construction sites'
    },
    {
      icon: Users,
      title: 'Your Team or Ours',
      description: 'Use internal staff or POPapp-certified photographers worldwide'
    },
    {
      icon: BarChart3,
      title: 'Instant Reports',
      description: 'Filter, compare, and export structured proof in seconds'
    },
    {
      icon: Shield,
      title: 'Full Accountability',
      description: 'Complete audit trail for every photo captured'
    }
  ];

  const steps = [
    {
      number: '01',
      title: 'Define Campaign',
      description: 'Create workflows, set frequencies, assign locations and users from HQ desktop'
    },
    {
      number: '02',
      title: 'Assign Teams',
      description: 'Deploy your field staff or book POPapp-certified photographers anywhere'
    },
    {
      number: '03',
      title: 'Capture Photos',
      description: 'Mobile app guides teams through structured photo capture with GPS and timestamps'
    },
    {
      number: '04',
      title: 'Review & Report',
      description: 'Dashboard delivers structured proof, comparisons, and exportable reports'
    }
  ];

  const stats = [
    { value: '10,000+', label: 'Locations Verified' },
    { value: '99.8%', label: 'Capture Accuracy' },
    { value: '3x', label: 'Faster Reporting' },
    { value: '45%', label: 'Cost Reduction' }
  ];

  return (
    <div className="min-h-screen bg-[#B6BCC3]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-[#0F4C81]/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img src="https://customer-assets.emergentagent.com/job_visual-verify-2/artifacts/tynlzwta_POPapp%20logo.png" alt="POPapp Logo" className="h-10 w-auto" />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <button onClick={() => scrollToSection('solutions')} className="text-[#1A2433] hover:text-[#1E74D6] transition-colors duration-200">Solutions</button>
            <button onClick={() => scrollToSection('industries')} className="text-[#1A2433] hover:text-[#1E74D6] transition-colors duration-200">Industries</button>
            <button onClick={() => scrollToSection('pricing')} className="text-[#1A2433] hover:text-[#1E74D6] transition-colors duration-200">Pricing</button>
            <button onClick={() => scrollToSection('contact')} className="text-[#1A2433] hover:text-[#1E74D6] transition-colors duration-200">Contact</button>
          </nav>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center space-x-4">
            <a href="mailto:sales@pop-app.net?subject=Request Demo" className="text-[#1E74D6] hover:text-[#0F4C81] transition-colors duration-200">Book a Demo</a>
            <a href="mailto:sales@pop-app.net?subject=Talk to Sales">
              <Button className="bg-gradient-to-r from-[#1E74D6] to-[#37A2FF] hover:from-[#0F4C81] hover:to-[#1E74D6] text-white shadow-lg hover:shadow-[#4FB4FF]/50 transition-all duration-300">
                Talk to Sales
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden text-[#0F1A2B]">
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-[#0F4C81]/10">
            <nav className="flex flex-col p-6 space-y-4">
              <button onClick={() => scrollToSection('solutions')} className="text-left text-[#1A2433] hover:text-[#1E74D6] transition-colors duration-200">Solutions</button>
              <button onClick={() => scrollToSection('industries')} className="text-left text-[#1A2433] hover:text-[#1E74D6] transition-colors duration-200">Industries</button>
              <button onClick={() => scrollToSection('pricing')} className="text-left text-[#1A2433] hover:text-[#1E74D6] transition-colors duration-200">Pricing</button>
              <button onClick={() => scrollToSection('contact')} className="text-left text-[#1A2433] hover:text-[#1E74D6] transition-colors duration-200">Contact</button>
              <a href="mailto:sales@pop-app.net?subject=Request Demo" className="text-left text-[#1E74D6]">Book a Demo</a>
              <a href="mailto:sales@pop-app.net?subject=Talk to Sales">
                <Button className="w-full bg-gradient-to-r from-[#1E74D6] to-[#37A2FF] text-white">Talk to Sales</Button>
              </a>
            </nav>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F4C81]/5 via-transparent to-[#37A2FF]/5"></div>
        <div className="absolute top-20 right-10 w-96 h-96 bg-[#4FB4FF]/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 left-10 w-80 h-80 bg-[#1E74D6]/20 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center relative z-10">
          <div className="space-y-6">
            <h1 className="text-5xl md:text-7xl font-bold text-[#0F1A2B] leading-tight">
              Eyes <span className="bg-gradient-to-r from-[#0F4C81] via-[#1E74D6] to-[#37A2FF] bg-clip-text text-transparent">Everywhere</span>
            </h1>
            <p className="text-2xl text-[#1A2433] font-semibold">Agility. Precision. Accountability.</p>
            <p className="text-lg text-[#1A2433] leading-relaxed">
              POPapp is a structured-photography and field-verification platform for organizations with multiple physical locations or assets. Transform every image into structured, traceable data with GPS, timestamp, user ID, and campaign metadata.
            </p>
            <p className="text-xl font-semibold text-[#0F4C81] italic">See everything. Control everything.</p>
            <div className="flex flex-wrap gap-4 pt-4">
              <a href="mailto:sales@pop-app.net?subject=Request Demo">
                <Button className="bg-gradient-to-r from-[#1E74D6] to-[#37A2FF] hover:from-[#0F4C81] hover:to-[#1E74D6] text-white px-8 py-6 text-lg shadow-xl hover:shadow-[#4FB4FF]/50 transition-all duration-300">
                  Book a Demo <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
              <a href="mailto:sales@pop-app.net?subject=Talk to Sales">
                <Button variant="outline" className="border-2 border-[#1E74D6] text-[#1E74D6] hover:bg-[#1E74D6] hover:text-white px-8 py-6 text-lg transition-all duration-300">
                  Talk to Sales
                </Button>
              </a>
            </div>
          </div>
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-[#0F4C81] via-[#1E74D6] to-[#37A2FF] rounded-3xl transform rotate-3 opacity-20 blur-2xl"></div>
            <img src="https://images.unsplash.com/photo-1523371683773-affcb4a2e39e" alt="POPapp Mobile Photography" className="relative rounded-3xl shadow-2xl w-full h-auto" />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 px-6 bg-white/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <div key={index} className="text-center space-y-2">
              <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-[#0F4C81] to-[#37A2FF] bg-clip-text text-transparent">{stat.value}</div>
              <div className="text-[#1A2433]">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto text-center space-y-6">
          <h2 className="text-4xl md:text-5xl font-bold text-[#0F1A2B]">The Problem: Operational Chaos</h2>
          <div className="grid md:grid-cols-3 gap-6 pt-8">
            <Card className="bg-white/80 backdrop-blur-sm border-[#1E74D6]/20 hover:border-[#1E74D6] transition-all duration-300 overflow-hidden group">
              <div className="relative h-48 overflow-hidden">
                <img src="https://images.unsplash.com/photo-1560068319-ac31024fdc6e" alt="WhatsApp Photos" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-red-900/80 to-transparent"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-16 h-16 bg-red-100 rounded-xl flex items-center justify-center">
                    <X className="w-8 h-8 text-red-600" />
                  </div>
                </div>
              </div>
              <CardContent className="p-6 space-y-3">
                <h3 className="font-semibold text-[#0F1A2B] text-lg">WhatsApp Photos</h3>
                <p className="text-sm text-[#1A2433]">Scattered across threads with no structure or metadata</p>
              </CardContent>
            </Card>
            <Card className="bg-white/80 backdrop-blur-sm border-[#1E74D6]/20 hover:border-[#1E74D6] transition-all duration-300 overflow-hidden group">
              <div className="relative h-48 overflow-hidden">
                <img src="https://images.unsplash.com/photo-1583521214690-73421a1829a9" alt="No Accountability" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-red-900/80 to-transparent"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-16 h-16 bg-red-100 rounded-xl flex items-center justify-center">
                    <X className="w-8 h-8 text-red-600" />
                  </div>
                </div>
              </div>
              <CardContent className="p-6 space-y-3">
                <h3 className="font-semibold text-[#0F1A2B] text-lg">No Accountability</h3>
                <p className="text-sm text-[#1A2433]">Missing GPS, timestamps, and user identification</p>
              </CardContent>
            </Card>
            <Card className="bg-white/80 backdrop-blur-sm border-[#1E74D6]/20 hover:border-[#1E74D6] transition-all duration-300 overflow-hidden group">
              <div className="relative h-48 overflow-hidden">
                <img src="https://images.unsplash.com/photo-1635859890085-ec8cb5466806" alt="Slow Reporting" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-red-900/80 to-transparent"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-16 h-16 bg-red-100 rounded-xl flex items-center justify-center">
                    <X className="w-8 h-8 text-red-600" />
                  </div>
                </div>
              </div>
              <CardContent className="p-6 space-y-3">
                <h3 className="font-semibold text-[#0F1A2B] text-lg">Slow Reporting</h3>
                <p className="text-sm text-[#1A2433]">Manual compilation wastes hours every week</p>
              </CardContent>
            </Card>
          </div>
          <div className="pt-8">
            <p className="text-2xl font-semibold text-[#1E74D6]">POPapp brings order through structured photography.</p>
          </div>
        </div>
      </section>

      {/* POPapp Overview */}
      <section id="solutions" className="py-20 px-6 bg-white/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto space-y-16">
          <div className="text-center space-y-4">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0F1A2B]">How POPapp Works</h2>
            <p className="text-xl text-[#1A2433] max-w-3xl mx-auto">Three simple steps to transform your field operations</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-white border-[#1E74D6]/20 hover:border-[#1E74D6] hover:shadow-xl hover:shadow-[#4FB4FF]/20 transition-all duration-300 group">
              <CardContent className="p-8 space-y-4">
                <div className="w-16 h-16 bg-gradient-to-br from-[#0F4C81] to-[#37A2FF] rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <BarChart3 className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-[#0F1A2B]">Design Campaigns at HQ</h3>
                <p className="text-[#1A2433]">Desktop app with workflows, frequencies, asset lists, and user assignments. Set up campaigns once and deploy globally.</p>
              </CardContent>
            </Card>

            <Card className="bg-white border-[#1E74D6]/20 hover:border-[#1E74D6] hover:shadow-xl hover:shadow-[#4FB4FF]/20 transition-all duration-300 group">
              <CardContent className="p-8 space-y-4">
                <div className="w-16 h-16 bg-gradient-to-br from-[#0F4C81] to-[#37A2FF] rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Camera className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-[#0F1A2B]">Capture Photos in the Field</h3>
                <p className="text-[#1A2433]">Mobile app for interiors/exteriors with GPS, timestamp, and audit rules. Works with internal teams or POPapp-certified photographers worldwide.</p>
              </CardContent>
            </Card>

            <Card className="bg-white border-[#1E74D6]/20 hover:border-[#1E74D6] hover:shadow-xl hover:shadow-[#4FB4FF]/20 transition-all duration-300 group">
              <CardContent className="p-8 space-y-4">
                <div className="w-16 h-16 bg-gradient-to-br from-[#0F4C81] to-[#37A2FF] rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-[#0F1A2B]">Get Structured Proof & Reports</h3>
                <p className="text-[#1A2433]">Dashboard with filtering, comparisons, and exportable reports. Every photo becomes searchable, traceable data.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto space-y-12">
          <div className="text-center space-y-4">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0F1A2B]">Why POPapp?</h2>
            <p className="text-xl text-[#1A2433]">Enterprise-grade field verification</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <Card key={index} className="bg-white/80 backdrop-blur-sm border-[#1E74D6]/20 hover:border-[#1E74D6] hover:shadow-xl hover:shadow-[#4FB4FF]/20 transition-all duration-300 group">
                <CardContent className="p-6 space-y-4">
                  <benefit.icon className="w-12 h-12 text-[#1E74D6] group-hover:text-[#37A2FF] transition-colors duration-300" />
                  <h3 className="text-xl font-bold text-[#0F1A2B]">{benefit.title}</h3>
                  <p className="text-[#1A2433]">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Steps */}
      <section className="py-20 px-6 bg-white/50 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto space-y-12">
          <div className="text-center space-y-4">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0F1A2B]">Simple. Operational. Effective.</h2>
          </div>

          <div className="space-y-8">
            {steps.map((step, index) => (
              <div key={index} className="flex gap-6 items-start group">
                <div className="flex-shrink-0 w-20 h-20 bg-gradient-to-br from-[#0F4C81] to-[#37A2FF] rounded-2xl flex items-center justify-center text-white text-2xl font-bold shadow-lg group-hover:shadow-[#4FB4FF]/50 group-hover:scale-110 transition-all duration-300">
                  {step.number}
                </div>
                <div className="flex-1 space-y-2 pt-2">
                  <h3 className="text-2xl font-bold text-[#0F1A2B]">{step.title}</h3>
                  <p className="text-lg text-[#1A2433]">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section id="industries" className="py-20 px-6">
        <div className="max-w-7xl mx-auto space-y-12">
          <div className="text-center space-y-4">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0F1A2B]">Built for Every Industry</h2>
            <p className="text-xl text-[#1A2433]">Cross-vertical field verification</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {industries.map((industry, index) => (
              <div 
                key={index} 
                className="relative"
                onMouseEnter={() => setHoveredIndustry(index)}
                onMouseLeave={() => setHoveredIndustry(null)}
              >
                <Card className="bg-white border-[#1E74D6]/20 hover:border-[#1E74D6] overflow-hidden group cursor-pointer transition-all duration-300 hover:shadow-2xl hover:shadow-[#4FB4FF]/30 hover:-translate-y-2">
                  <div className="relative h-48 overflow-hidden">
                    <img src={industry.image} alt={industry.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0F4C81]/80 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <industry.icon className="w-10 h-10 text-white mb-2" />
                      <h3 className="text-xl font-bold text-white">{industry.title}</h3>
                    </div>
                  </div>
                  <CardContent className="p-6 space-y-3">
                    <div className="flex items-start gap-2">
                      <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-[#1A2433]">{industry.pain}</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-[#1E74D6] flex-shrink-0 mt-0.5" />
                      <p className="text-sm font-semibold text-[#0F1A2B]">{industry.benefit}</p>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Hover Popup with Case Studies */}
                {hoveredIndustry === index && (
                  <div className="absolute left-0 right-0 top-full mt-2 z-50 animate-in fade-in slide-in-from-top-2 duration-300">
                    <div className="bg-gradient-to-br from-[#0F4C81] via-[#1E74D6] to-[#37A2FF] rounded-2xl p-6 shadow-2xl">
                      <div className="flex items-center gap-3 mb-4 pb-3 border-b border-white/20">
                        <industry.icon className="w-6 h-6 text-white" />
                        <h4 className="text-lg font-bold text-white">Typical Use Cases</h4>
                      </div>
                      <ul className="space-y-2.5">
                        {industry.caseStudies.map((caseStudy, caseIndex) => (
                          <li key={caseIndex} className="flex items-start gap-2.5 text-white">
                            <CheckCircle2 className="w-4 h-4 text-[#4FB4FF] flex-shrink-0 mt-0.5" />
                            <span className="text-sm leading-relaxed">{caseStudy}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-6 bg-white/50 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto space-y-12">
          <div className="text-center space-y-4">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0F1A2B]">Flexible Pricing Models</h2>
            <p className="text-xl text-[#1A2433]">Choose the model that fits your operations</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="bg-white border-[#1E74D6]/20 hover:border-[#1E74D6] hover:shadow-2xl hover:shadow-[#4FB4FF]/30 transition-all duration-300 group flex flex-col h-full">
              <CardContent className="p-10 flex flex-col h-full">
                <div className="space-y-6 flex-1">
                  <div className="space-y-2">
                    <h3 className="text-3xl font-bold text-[#0F1A2B]">Software Only</h3>
                    <p className="text-[#1A2433]">Use POPapp with your own field teams and store personnel</p>
                  </div>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#1E74D6] flex-shrink-0 mt-0.5" />
                      <span className="text-[#1A2433]">Desktop campaign builder</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#1E74D6] flex-shrink-0 mt-0.5" />
                      <span className="text-[#1A2433]">Mobile app for field teams</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#1E74D6] flex-shrink-0 mt-0.5" />
                      <span className="text-[#1A2433]">Structured data & reporting dashboard</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#1E74D6] flex-shrink-0 mt-0.5" />
                      <span className="text-[#1A2433]">Unlimited campaigns & locations</span>
                    </li>
                  </ul>
                </div>
                <a href="mailto:sales@pop-app.net?subject=Software Only Pricing" className="mt-8">
                  <Button className="w-full bg-gradient-to-r from-[#1E74D6] to-[#37A2FF] hover:from-[#0F4C81] hover:to-[#1E74D6] text-white py-6 text-lg group-hover:shadow-xl group-hover:shadow-[#4FB4FF]/50 transition-all duration-300">
                    Request Pricing
                  </Button>
                </a>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-[#0F4C81] to-[#1E74D6] border-none hover:shadow-2xl hover:shadow-[#4FB4FF]/50 transition-all duration-300 group flex flex-col h-full">
              <CardContent className="p-10 flex flex-col h-full">
                <div className="space-y-6 flex-1">
                  <div className="space-y-2">
                    <h3 className="text-3xl font-bold text-white">Software + POPapp Photographers</h3>
                    <p className="text-white/90">Complete solution with certified photographers worldwide</p>
                  </div>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#4FB4FF] flex-shrink-0 mt-0.5" />
                      <span className="text-white">Everything in Software Only</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#4FB4FF] flex-shrink-0 mt-0.5" />
                      <span className="text-white">POPapp-certified photographers anywhere</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#4FB4FF] flex-shrink-0 mt-0.5" />
                      <span className="text-white">On-demand deployment</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#4FB4FF] flex-shrink-0 mt-0.5" />
                      <span className="text-white">Quality-controlled execution</span>
                    </li>
                  </ul>
                </div>
                <a href="mailto:sales@pop-app.net?subject=Full Service Pricing" className="mt-8">
                  <Button className="w-full bg-white hover:bg-white/90 text-[#0F4C81] py-6 text-lg font-bold group-hover:shadow-xl transition-all duration-300">
                    Request Pricing
                  </Button>
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section id="contact" className="py-20 px-6 bg-gradient-to-br from-[#0F4C81] via-[#1E74D6] to-[#37A2FF] relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjEiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-30"></div>
        
        <div className="max-w-4xl mx-auto text-center space-y-8 relative z-10">
          <h2 className="text-4xl md:text-6xl font-bold text-white">Turn Field Photos Into Structured Operational Proof</h2>
          <p className="text-xl text-white/90">Join leading enterprises using POPapp for field verification</p>
          <div className="flex flex-wrap gap-4 justify-center pt-4">
            <a href="mailto:sales@pop-app.net?subject=Request Demo">
              <Button className="bg-white hover:bg-white/90 text-[#0F4C81] px-10 py-6 text-lg font-bold shadow-2xl hover:shadow-white/50 transition-all duration-300">
                Book a Demo <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
            <a href="mailto:sales@pop-app.net?subject=Talk to Sales">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#0F4C81] px-10 py-6 text-lg font-bold transition-all duration-300">
                Talk to Sales
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0F1A2B] text-white py-12 px-6">
        <div className="max-w-7xl mx-auto grid md:grid-cols-4 gap-8">
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <img src="https://customer-assets.emergentagent.com/job_visual-verify-2/artifacts/tynlzwta_POPapp%20logo.png" alt="POPapp Logo" className="h-10 w-auto brightness-0 invert" />
            </div>
            <p className="text-white/70">Structured photography for distributed operations</p>
          </div>
          
          <div className="space-y-3">
            <h3 className="font-bold text-lg">Product</h3>
            <ul className="space-y-2 text-white/70">
              <li><button onClick={() => scrollToSection('solutions')} className="hover:text-[#4FB4FF] transition-colors">Solutions</button></li>
              <li><button onClick={() => scrollToSection('industries')} className="hover:text-[#4FB4FF] transition-colors">Industries</button></li>
              <li><button onClick={() => scrollToSection('pricing')} className="hover:text-[#4FB4FF] transition-colors">Pricing</button></li>
            </ul>
          </div>
          
          <div className="space-y-3">
            <h3 className="font-bold text-lg">Company</h3>
            <ul className="space-y-2 text-white/70">
              <li><a href="mailto:info@pop-app.net" className="hover:text-[#4FB4FF] transition-colors">About Us</a></li>
              <li><a href="mailto:careers@pop-app.net" className="hover:text-[#4FB4FF] transition-colors">Careers</a></li>
              <li><a href="mailto:support@pop-app.net" className="hover:text-[#4FB4FF] transition-colors">Support</a></li>
            </ul>
          </div>
          
          <div className="space-y-3">
            <h3 className="font-bold text-lg">Contact</h3>
            <ul className="space-y-2 text-white/70">
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                <a href="mailto:sales@pop-app.net" className="hover:text-[#4FB4FF] transition-colors">sales@pop-app.net</a>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto mt-12 pt-8 border-t border-white/10 text-center text-white/50">
          <p>&copy; 2025 POPapp. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Home;